import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { TimeField } from '@mui/x-date-pickers/TimeField'
import { Box } from '../../../../components/Box.tsx'
import { Typography } from '../../../../components/generic.tsx'
import { NumberInput } from '../../../../components/NumberInput.tsx'
import { type Dayjs, dayjs } from '../../../../dayjs.ts'
import { ids } from '../../../../ids.ts'
import { useDateFormat } from '../../../preferences/useFormatDate.tsx'

export const DateTimeField = ({
	value,
	timezone,
	min,
	max,
	onChange,
	onBlur,
	disabled,
	error,
	precision = 'milliseconds',
}: {
	value: Dayjs | null
	timezone: string
	onChange: (value: Dayjs) => void
	onBlur?: () => void
	min?: Dayjs
	max?: Dayjs
	disabled?: boolean
	error?: string
	// The smallest unit the user can enter; smaller units are zeroed.
	precision?: 'minutes' | 'milliseconds'
}) => {
	const format = useDateFormat()
	const withMilliseconds = precision === 'milliseconds'
	const onChangeValid = (newValue: Dayjs | null) => {
		if (newValue?.isValid()) {
			onChange(withMilliseconds ? newValue : newValue.startOf('minute'))
		}
	}

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box flexGrow={1}>
				<Box
					display='grid'
					gridTemplateColumns={
						withMilliseconds ? 'minmax(150px, 1fr) minmax(100px, 0.5fr) minmax(90px, 0.33fr)' : 'minmax(150px, 1fr) minmax(100px, 0.5fr)'
					}
					gap={1}
					center
				>
					{/* The DatePicker is broken: typing a date can also change the time, seemingly due to timezone offsets.
					The fix is probably to make these inputs not timezone aware. */}
					<DatePicker
						value={value}
						timezone={timezone}
						minDate={min}
						maxDate={max}
						disabled={disabled}
						slotProps={{ textField: { size: 'small', error: !!error, onBlur } }}
						onClose={onBlur}
						onChange={onChangeValid}
						format={format('DD.MM.YYYY')}
					/>
					<TimeField
						value={value}
						timezone={timezone}
						minTime={min}
						maxTime={max}
						disabled={disabled || !value}
						slotProps={{
							textField: {
								size: 'small',
								error: !!error,
								onBlur,
								// @ts-expect-error: missing data-ta prop in their types
								'data-ta': ids.reports.form.timeRange.timeField,
							},
						}}
						onClose={onBlur}
						ampm={false}
						onChange={onChangeValid}
						format={format(withMilliseconds ? 'HH.mm.ss' : 'HH.mm')}
					/>
					{withMilliseconds && (
						<NumberInput
							value={value ? dayjs(value).millisecond() : undefined}
							min={0}
							max={999}
							disabled={disabled || !value}
							error={!!error}
							onChangeCommited={(ms) => {
								onChange((value ?? dayjs()).millisecond(ms ?? 0))
							}}
							onBlur={onBlur}
							endSymbol='ms'
							showStepButtons={false}
							allowEmpty={!value}
							dataTa={ids.reports.form.lightning.timeRange.msField}
						/>
					)}
				</Box>
				{error && <Typography color='error'>{error}</Typography>}
			</Box>
		</LocalizationProvider>
	)
}
